// src/v3/ui/pinbar/pinbar-page.tsx

import type { ColumnDef } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { FileText, Loader2, Sparkles } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  Button,
  DataPage,
  JobRunForm,
  mergePresetLists,
  Modal,
  parseUrlState,
  Progress,
  qk,
  Select,
  serializeUrlState,
  SmartTable,
  ToastContainer,
  useEvents,
  useToast,
  type JobParamField,
  type SmartTableColumnMeta,
} from "../_common";
import {
  APPROVE_STATUS_VARIANTS,
  ISO_DATE_TIME_SEPARATOR,
  PINBAR_IDENTIFY_RESULT_STATUS_OBJ,
  PINBAR_STATUS_VARIANTS,
  PINBAR_TYPE_VARIANTS,
} from "../_deps";
import { t } from "../locales";
import { pinbarPresets } from "../table-presets";
import { PinbarDetail } from "./pinbar-detail";
import {
  useExportPinbars,
  useIdentifyPinbars,
  usePinbarPrompt,
  usePinbars,
  useResetAiVerification,
  useUpdatePinbar,
  useUpdatePinbarPrompt,
  useVerifyPinbarsAiByDate,
  useVerifyPinbarsAiById,
  type PinbarItem,
} from "./pinbar.hooks";

const PROGRESS_RESET_DELAY_MS = 1_000;

function parsePinbarUpdate(
  field: string,
  value: unknown,
): Partial<PinbarItem> | null {
  if (field === "comment" && (typeof value === "string" || value === null)) {
    return { comment: value };
  }
  if (field === "pinbarStatus" && typeof value === "string") {
    return { pinbarStatus: value };
  }
  if (field === "approveStatus" && typeof value === "string") {
    return { approveStatus: value };
  }
  return null;
}

const identifyFields: JobParamField[] = [
  { name: "barDate", type: "date", required: true },
  {
    name: "aiVerification",
    type: "boolean",
    required: false,
    label: "AI Verification",
    defaultValue: false,
  },
  {
    name: "candleMode",
    type: "enum",
    required: false,
    label: "Candle Mode",
    options: ["single", "multi"],
    description: "single = daily candles only; multi = daily + weekly + monthly",
  },
];

export function PinbarPage() {
  const queryClient = useQueryClient();
  const { toasts, dismissToast, showToast } = useToast();
  const { events } = useEvents(["pinbar.identify.progress"]);

  const [date, setDate] = useState<string | null>(() => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setUTCDate(yesterday.getUTCDate() - 1);
    return yesterday.toISOString().split("T")[0] ?? "";
  });
  const [selected, setSelected] = useState<PinbarItem | null>(null);
  const [selectedRows, setSelectedRows] = useState<PinbarItem[]>([]);
  void selectedRows; // used by onSelectedRowsChange for bulk edit tracking
  const [progress, setProgress] = useState<number | null>(null);
  const [identifyModalOpen, setIdentifyModalOpen] = useState(false);
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [promptContent, setPromptContent] = useState("");
  const [promptVersion, setPromptVersion] = useState(0);
  const [aiCandleMode, setAiCandleMode] = useState("single");
  const [aiVerifyTarget, setAiVerifyTarget] = useState<PinbarItem | null>(null);
  const [aiVerifyByDateTarget, setAiVerifyByDateTarget] = useState<string | null>(null);
  const [aiVerifyModalMode, setAiVerifyModalMode] = useState("single");

  const [urlState, setUrlState] = useState(() =>
    parseUrlState(new URLSearchParams(window.location.search)),
  );

  const [visibleColumnIds, setVisibleColumnIds] = useState<string[] | null>(
    null,
  );
  const [stickyColumns, setStickyColumns] = useState<string[]>(
    urlState.stickyColumns ?? [],
  );
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(
    urlState.hiddenColumns ?? [],
  );

  // ── Server-state reads (TanStack Query) ───────────────────────────────────
  const pinbarsQuery = usePinbars({
    ...(date ? { date } : {}),
    page: urlState.page,
    pageSize: urlState.pageSize,
    filters: urlState.filters,
    sort: urlState.sort,
  });
  const items = pinbarsQuery.data?.items ?? [];
  const total = pinbarsQuery.data?.total ?? 0;
  const isLoading = pinbarsQuery.isLoading;
  const error = pinbarsQuery.error?.message ?? null;

  const promptQuery = usePinbarPrompt(promptModalOpen);

  // ── Server-state mutations (TanStack Query) ───────────────────────────────
  const identifyMutation = useIdentifyPinbars();
  const verifyByDateMutation = useVerifyPinbarsAiByDate();
  const verifyByIdMutation = useVerifyPinbarsAiById();
  const updatePinbarMutation = useUpdatePinbar();
  const resetAiMutation = useResetAiVerification();
  const updatePromptMutation = useUpdatePinbarPrompt();
  const exportMutation = useExportPinbars();

  const isIdentifying = identifyMutation.isPending;
  const isAiVerifying =
    verifyByDateMutation.isPending || verifyByIdMutation.isPending;
  const promptLoading = promptQuery.isLoading;
  const promptSaving = updatePromptMutation.isPending;

  // Sync prompt draft from server when modal opens / data arrives
  useEffect(() => {
    if (promptModalOpen && promptQuery.data) {
      setPromptContent(promptQuery.data.content ?? "");
      setPromptVersion(promptQuery.data.version ?? 0);
    }
  }, [promptModalOpen, promptQuery.data]);

  const refreshData = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: qk.pinbar.list() });
  }, [queryClient]);

  useEffect(() => {
    if (events.length === 0) return;
    const latest = events[events.length - 1];
    if (!latest) return;
    if (latest.progress === 100) {
      setProgress(null);
    } else {
      setProgress(latest.progress);
    }
  }, [events]);

  useEffect(() => {
    const newUrl = `${window.location.pathname}${serializeUrlState(urlState)}`;
    window.history.replaceState(null, "", newUrl);
  }, [urlState]);

  useEffect(() => {
    const preset = pinbarPresets.find((p) => p.id === urlState.preset);
    setVisibleColumnIds(preset ? preset.columns : null);
    setStickyColumns(preset?.stickyColumns ?? []);
    setHiddenColumns(preset?.hiddenColumns ?? []);
  }, [urlState.preset]);

  useEffect(() => {
    setSelected(null);
  }, [urlState.filters]);

  // Sync selected pinbar with updated items list (e.g. after AI verify)
  useEffect(() => {
    if (selected) {
      const updated = items.find((p) => p.id === selected.id);
      if (updated && updated !== selected) {
        setSelected(updated);
      }
    }
  }, [items, selected]);

  const handleIdentify = useCallback(
    async (params: Record<string, unknown>) => {
      const barDate = String(params["barDate"] ?? "");
      const aiVerification = params["aiVerification"] === true;
      const skipAi = !aiVerification;
      const candleModeValue = params["candleMode"];
      const candleMode =
        typeof candleModeValue === "string" ? candleModeValue : "single";
      setProgress(0);
      try {
        const result = await identifyMutation.mutateAsync({
          date: barDate,
          skipAi,
          candleMode,
        });
        setIdentifyModalOpen(false);
        const status = result?.status ?? "";
        const count = result?.items?.length ?? 0;
        if (status === PINBAR_IDENTIFY_RESULT_STATUS_OBJ.NO_PINBARS_FOUND || count === 0) {
          showToast("info", t("pinbar.identify.noPinbars", { date: barDate }));
        } else {
          showToast(
            "success",
            t("pinbar.identify.success", { count, date: barDate }),
          );
        }
      } catch (e) {
        const message = e instanceof Error ? e.message : "Unknown error";
        showToast(
          "error",
          t("pinbar.identify.error", { message }),
          () => void handleIdentify(params),
        );
      } finally {
        setTimeout(() => setProgress(null), PROGRESS_RESET_DELAY_MS);
      }
    },
    [identifyMutation, showToast],
  );

  const handleBulkSave = useCallback(
    async (edits: Map<string, Partial<PinbarItem>>) => {
      const payload = Array.from(edits.entries()).map(([id, fields]) => ({
        id,
        fields,
      }));
      try {
        await Promise.all(
          payload.map((p) => updatePinbarMutation.mutateAsync(p)),
        );
        showToast("success", `Updated ${payload.length} pinbars`);
      } catch (e) {
        const message = e instanceof Error ? e.message : "Unknown error";
        showToast("error", message);
      }
    },
    [updatePinbarMutation, showToast],
  );

  const openPromptModal = useCallback(() => {
    setPromptModalOpen(true);
    setPromptContent("");
    setPromptVersion(0);
  }, []);

  const handleSavePrompt = useCallback(async () => {
    if (!promptContent.trim()) {
      showToast("error", "Prompt content must not be empty");
      return;
    }
    try {
      const data = await updatePromptMutation.mutateAsync({
        content: promptContent,
      });
      setPromptVersion(data?.version ?? promptVersion);
      showToast("success", "AI prompt saved — active for next identify");
      setPromptModalOpen(false);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Unknown error";
      showToast("error", message);
    }
  }, [promptContent, promptVersion, updatePromptMutation, showToast]);

  const runVerifyById = useCallback(
    (pinbarId: string, candleMode: string) => {
      verifyByIdMutation.mutate(
        { pinbarId, candleMode },
        {
          onSuccess: () => {
            showToast(
              "success",
              t("pinbar.identify.aiVerifySuccess", { count: 1 }),
            );
          },
          onError: (e) => {
            const message = e instanceof Error ? e.message : "Unknown error";
            showToast("error", t("pinbar.identify.aiVerifyError", { message }));
          },
        },
      );
    },
    [verifyByIdMutation, showToast],
  );

  const runVerifyByDate = useCallback(
    (targetDate: string, candleMode: string) => {
      verifyByDateMutation.mutate(
        { date: targetDate, candleMode },
        {
          onSuccess: (result) => {
            showToast(
              "success",
              t("pinbar.identify.aiVerifySuccess", {
                count: result?.verifiedCount ?? 0,
              }),
            );
          },
          onError: (e) => {
            const message = e instanceof Error ? e.message : "Unknown error";
            showToast("error", t("pinbar.identify.aiVerifyError", { message }));
          },
        },
      );
    },
    [verifyByDateMutation, showToast],
  );

  const columns: ColumnDef<PinbarItem>[] = [
    { accessorKey: "symbol", header: "Symbol" },
    { accessorKey: "symbolStatus", header: "Symbol Status" },
    {
      accessorKey: "type",
      header: "Type",
      meta: {
        format: "chip",
        formatOptions: {
          variants: PINBAR_TYPE_VARIANTS,
        },
      } satisfies SmartTableColumnMeta,
    },
    {
      accessorKey: "date",
      header: "Signal Date",
      meta: { format: "date" } satisfies SmartTableColumnMeta,
    },
    {
      accessorKey: "pinbarStatus",
      header: "Pinbar Status",
      meta: {
        editable: true,
        format: "chip",
        formatOptions: {
          variants: PINBAR_STATUS_VARIANTS,
        },
      } satisfies SmartTableColumnMeta,
    },
    {
      accessorKey: "approveStatus",
      header: "Approve Status",
      meta: {
        editable: true,
        format: "chip",
        formatOptions: {
          variants: APPROVE_STATUS_VARIANTS,
        },
      } satisfies SmartTableColumnMeta,
    },
    { accessorKey: "strategyVersion", header: "Strategy Version" },
    {
      accessorKey: "comment",
      header: "Comment",
      meta: { editable: true } satisfies SmartTableColumnMeta,
    },
    {
      accessorKey: "id",
      header: "ID",
      meta: { format: "id" } satisfies SmartTableColumnMeta,
    },
    {
      id: "timestamp.createdAt",
      accessorFn: (row) => row.timestamp?.createdAt,
      header: "Created At",
      meta: { format: "datetime" } satisfies SmartTableColumnMeta,
    },
    {
      id: "timestamp.updatedAt",
      accessorFn: (row) => row.timestamp?.updatedAt,
      header: "Updated At",
      meta: { format: "datetime" } satisfies SmartTableColumnMeta,
    },
    {
      id: "actions",
      meta: {
        filterable: "none",
        sortable: "none",
        isAction: true,
      } satisfies SmartTableColumnMeta,
      cell: ({ row }) => (
        <div className="flex gap-0.5" onClick={(e) => e.stopPropagation()}>
          <Button
            size="icon"
            variant="outline"
            className="h-6 w-6 border-violet-400 text-violet-600 hover:bg-violet-50 p-0"
            disabled={isAiVerifying}
            onClick={() => runVerifyById(row.original.id, "single")}
            aria-label="AI verify (single)"
            title="AI verify — single mode"
          >
            {isAiVerifying
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Sparkles className="h-3.5 w-3.5" />}
          </Button>
          <Button
            size="icon"
            variant="outline"
            className="h-6 w-6 border-violet-400 text-violet-600 hover:bg-violet-50 p-0 border-l-0"
            disabled={isAiVerifying}
            onClick={() => runVerifyById(row.original.id, "multi")}
            aria-label="AI verify (multi)"
            title="AI verify — multi mode"
          >
            {isAiVerifying
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Sparkles className="h-3.5 w-3.5" />}
            <span className="text-[8px] font-bold leading-none">M</span>
          </Button>
        </div>
      ),
      header: "Actions",
    },
  ];

  const handlePresetChange = (presetId: string) => {
    const preset = pinbarPresets.find((p) => p.id === presetId);
    if (preset) {
      setUrlState({
        page: 1,
        pageSize: preset.pageSize,
        filters: preset.filters,
        sort: preset.sort,
        preset: presetId,
      });
    }
  };

  return (
    <div className="space-y-4">
      <DataPage
        title="Pinbars"
        isLoading={isLoading}
        error={error}
        actions={
          <>
            <input
              type="date"
              value={date ?? ""}
              onChange={(e) => setDate(e.target.value || null)}
              className="rounded-md border px-3 py-2 text-sm"
              aria-label="Filter by date"
            />
            {date !== null && (
              <Button variant="ghost" size="sm" onClick={() => setDate(null)}>
                All dates
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => refreshData()}>
              Refresh
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => setIdentifyModalOpen(true)}
            >
              {t("pinbar.identify")}
            </Button>
            {date !== null && (
              <Button
                variant="outline"
                size="sm"
                disabled={isAiVerifying}
                onClick={() => {
                  setAiVerifyModalMode(aiCandleMode);
                  setAiVerifyByDateTarget(date);
                }}
              >
                {isAiVerifying
                  ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  : <Sparkles className="h-4 w-4 mr-1" />}
                {t("pinbar.identify.aiVerify")}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => void openPromptModal()}
            >
              <FileText className="h-4 w-4 mr-1" />
              AI Prompt
            </Button>
          </>
        }
      >
        <SmartTable
          columns={columns}
          data={items}
          total={total}
          page={urlState.page}
          pageSize={urlState.pageSize}
          isLoading={false}
          error={null}
          filters={urlState.filters}
          sort={urlState.sort}
          presets={pinbarPresets}
          activePresetId={urlState.preset}
          visibleColumnIds={visibleColumnIds}
          stickyColumns={stickyColumns}
          hiddenColumns={hiddenColumns}
          columnGroups={
            pinbarPresets.find((p) => p.id === urlState.preset)?.columnGroups ??
            []
          }
          columnMeta={
            pinbarPresets.find((p) => p.id === urlState.preset)?.columnMeta ??
            {}
          }
          lists={mergePresetLists(pinbarPresets, urlState.preset)}
          onExport={async () => {
            try {
              const result = await exportMutation.mutateAsync({
                date: date ?? "",
              });
              const normalizedDate = (date ?? "").includes(ISO_DATE_TIME_SEPARATOR)
                ? (date ?? "").split(ISO_DATE_TIME_SEPARATOR)[0]!
                : (date ?? "");
              const blob = new Blob([JSON.stringify(result, null, 2)], {
                type: "application/json",
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `pinbars-${normalizedDate || "all"}.json`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            } catch (e) {
              const message = e instanceof Error ? e.message : "Failed to export";
              showToast("error", message);
            }
          }}
          exportLabel="Export"
          onVisibleColumnsChange={(ids) => setVisibleColumnIds(ids)}
          onPageChange={(page) => setUrlState((prev) => ({ ...prev, page }))}
          onPageSizeChange={(pageSize) =>
            setUrlState((prev) => ({ ...prev, pageSize, page: 1 }))
          }
          onFiltersChange={(filters) =>
            setUrlState((prev) => ({ ...prev, filters, page: 1 }))
          }
          onSortChange={(sort) => setUrlState((prev) => ({ ...prev, sort }))}
          onPresetChange={handlePresetChange}
          onRowClick={(row) => setSelected(row)}
          enableBulkEdit
          onBulkSave={handleBulkSave}
          onSelectedRowsChange={setSelectedRows}
          onInlineEdit={async (rowId, field, value) => {
            const fields = parsePinbarUpdate(field, value);
            if (!fields) return;
            await updatePinbarMutation.mutateAsync({
              id: rowId,
              fields,
            });
          }}
        />
        {progress !== null && (
          <Progress
            value={progress}
            label={
              events.length > 0 && events[events.length - 1]?.message
                ? events[events.length - 1]!.message!
                : "Processing..."
            }
          />
        )}
      </DataPage>
      <Modal
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={
          selected !== null
            ? `${selected.symbol} — ${selected.type}`
            : undefined
        }
      >
        {selected !== null && (
          <PinbarDetail
            pinbar={selected}
            onUpdateField={async (id, field, value) => {
              const fields = parsePinbarUpdate(field, value);
              if (!fields) return;
              await updatePinbarMutation.mutateAsync({
                id,
                fields,
              });
              setSelected((prev) => (prev ? { ...prev, ...fields } : prev));
            }}
            onVerifyAi={async () => {
              const target = selected;
              if (!target) return;
              setAiVerifyModalMode(aiCandleMode);
              setAiVerifyTarget(target);
            }}
            onResetAi={async (id) => {
              await resetAiMutation.mutateAsync({ id });
              setSelected((prev) =>
                prev
                  ? {
                      ...prev,
                      approveStatus: "new",
                      reasoningText: null,
                      strategyVersion: 1,
                      context: null,
                    }
                  : prev,
              );
              showToast("success", "AI verification reset");
            }}
            isAiVerifying={isAiVerifying}
            aiProgress={
              progress !== null
                ? {
                    value: progress,
                    label:
                      events.length > 0 && events[events.length - 1]?.message
                        ? events[events.length - 1]!.message!
                        : undefined,
                  }
                : undefined
            }
          />
        )}
      </Modal>
      <Modal
        open={identifyModalOpen}
        onClose={() => !isIdentifying && setIdentifyModalOpen(false)}
        title={t("pinbar.identify.title")}
      >
        <JobRunForm
          fields={identifyFields}
          onRun={handleIdentify}
          isRunning={isIdentifying}
        />
      </Modal>
      <Modal
        open={promptModalOpen}
        onClose={() => !promptSaving && setPromptModalOpen(false)}
        title="AI Analysis Prompt"
      >
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            {promptLoading
              ? "Loading…"
              : promptVersion > 0
                ? `Active version: v${promptVersion}`
                : "No active prompt — AI verification will be skipped"}
          </div>
          <textarea
            className="w-full min-h-[300px] rounded-md border p-3 text-sm font-mono"
            value={promptContent}
            onChange={(e) => setPromptContent(e.target.value)}
            placeholder="Enter the AI analysis prompt for pinbar verification…"
            disabled={promptLoading || promptSaving}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPromptModalOpen(false)}
              disabled={promptSaving}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => void handleSavePrompt()}
              disabled={promptLoading || promptSaving || !promptContent.trim()}
            >
              {promptSaving ? "Saving…" : "Save & Activate"}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        open={aiVerifyTarget !== null || aiVerifyByDateTarget !== null}
        onClose={() => {
          setAiVerifyTarget(null);
          setAiVerifyByDateTarget(null);
        }}
        title="AI Verify"
      >
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            {aiVerifyTarget
              ? <>Pinbar: <span className="font-mono">{aiVerifyTarget.symbol}</span> ({aiVerifyTarget.date})</>
              : aiVerifyByDateTarget
                ? <>Date: <span className="font-mono">{aiVerifyByDateTarget}</span></>
                : null}
          </div>
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">Candle Mode</label>
            <Select
              value={aiVerifyModalMode}
              onChange={setAiVerifyModalMode}
              options={[
                { value: "single", label: "single — daily candles only" },
                { value: "multi", label: "multi — daily + weekly + monthly" },
              ]}
              ariaLabel="Candle Mode"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setAiVerifyTarget(null);
                setAiVerifyByDateTarget(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                setAiCandleMode(aiVerifyModalMode);
                if (aiVerifyTarget) {
                  const target = aiVerifyTarget;
                  setAiVerifyTarget(null);
                  runVerifyById(target.id, aiVerifyModalMode);
                } else if (aiVerifyByDateTarget) {
                  const targetDate = aiVerifyByDateTarget;
                  setAiVerifyByDateTarget(null);
                  runVerifyByDate(targetDate, aiVerifyModalMode);
                }
              }}
            >
              <Sparkles className="h-4 w-4 mr-1" />
              Run AI Verify
            </Button>
          </div>
        </div>
      </Modal>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
